import { useNavigate } from '@tanstack/react-router';
import { Archive, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  EntityKey,
  ErrorState,
  SkeletonRows,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { CaseAttributesCard } from '../case-attributes/CaseAttributesCard';
import { EnvironmentsCard } from '../environments/EnvironmentsCard';
import { LabelsCard } from '../labels/LabelsCard';
import { ProjectFormDialog } from './ProjectFormDialog';
import { ProjectIcon } from './ProjectIcon';
import { useDeleteProject, useProject, useSetProjectStatus } from './api';

export const ProjectSettingsPage = ({ projectCode }: { projectCode: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { dateTime } = useFormat();
  const { projectStatus } = useEnumLabels();
  const { errorText } = useErrorText();
  const { data: project, isPending, error, refetch } = useProject(projectCode);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const setStatus = useSetProjectStatus(projectCode);
  const deleteProject = useDeleteProject();

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !project) return <SkeletonRows rows={3} className="p-0" />;

  const toggleStatus = async () => {
    try {
      await setStatus.mutateAsync(project.status === 'active' ? 'archive' : 'restore');
      toast.success(
        project.status === 'active'
          ? t('projects.archived', { code: project.code })
          : t('projects.restored', { code: project.code }),
      );
    } catch (statusError) {
      toast.error(errorText(statusError, 'projects.statusChangeFailed'));
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteProject.mutateAsync(projectCode);
      toast.success(t('projects.deleted', { code: projectCode }));
      await navigate({ to: '/projects' });
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'projects.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader title={t('settings.title')} description={t('settings.subtitle')} />

      <Card>
        <CardHeader
          title={t('settings.details')}
          action={
            can('project:write') ? (
              <Button size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="size-3.5" />
                {t('common.edit')}
              </Button>
            ) : null
          }
        />
        <CardBody>
          {/* The same four-column grid the Details block of a test case uses, so
              properties line up instead of leaving holes; the description is
              prose and takes the full width under them. */}
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('common.name')}
              </dt>
              {/* The icon is part of how the project introduces itself, so it
                  stands with the name rather than in a line of its own. */}
              <dd className="mt-1 flex items-center gap-2 text-[13px] text-ink">
                <ProjectIcon icon={project.icon} color={project.iconColor} />
                {project.name}
              </dd>
            </div>
            <div>
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('projects.fieldCode')}
              </dt>
              <dd className="mt-1">
                <EntityKey className="text-[13px] text-ink">{project.code}</EntityKey>
              </dd>
            </div>
            <div>
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('common.status')}
              </dt>
              <dd
                className={
                  project.status === 'active'
                    ? 'mt-1 text-[13px] font-medium text-pass'
                    : 'mt-1 text-[13px] font-medium text-ink-subtle'
                }
              >
                {projectStatus(project.status)}
              </dd>
            </div>
            <div>
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('common.created')}
              </dt>
              <dd className="mt-1 text-[13px] text-ink">
                {dateTime(project.createdAt)}
                {project.createdBy ? ` · ${project.createdBy.name}` : ''}
              </dd>
            </div>
            <div className="sm:col-span-2 xl:col-span-4">
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('common.description')}
              </dt>
              <dd className="mt-1 whitespace-pre-line text-[13px] text-ink">
                {project.description ?? t('common.empty')}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <LabelsCard projectCode={projectCode} />

      <EnvironmentsCard projectCode={projectCode} />

      <CaseAttributesCard projectCode={projectCode} kind="layer" />

      <CaseAttributesCard projectCode={projectCode} kind="module" />

      {can('project:write') ? (
        <Card className="border-fail/30">
          <CardHeader
            title={t('settings.lifecycle')}
            description={t('settings.lifecycleHint')}
          />
          <CardBody className="flex flex-wrap gap-2">
            <Button onClick={() => void toggleStatus()} loading={setStatus.isPending}>
              {project.status === 'active' ? (
                <>
                  <Archive className="size-3.5" />
                  {t('projects.archive')}
                </>
              ) : (
                <>
                  <RotateCcw className="size-3.5" />
                  {t('projects.restore')}
                </>
              )}
            </Button>
            {can('project:delete') ? (
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="size-3.5" />
                {t('projects.deleteAction')}
              </Button>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <ProjectFormDialog open={editOpen} onOpenChange={setEditOpen} project={project} />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('projects.deleteTitle', { name: project.name })}
        description={t('projects.deleteHintCounts', {
          cases: t('projects.countCases', { count: project.counts.testCases }),
          checklists: t('projects.countChecklists', { count: project.counts.checklists }),
          runs: t('projects.countRuns', { count: project.counts.testRuns }),
        })}
        confirmLabel={t('projects.deleteAction')}
        loading={deleteProject.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
